/**
 * Parse a timestamp string from the API as UTC.
 *
 * The backend sends naive ISO timestamps (no timezone suffix) from a
 * server running in UTC.  Without the trailing "Z", `new Date()` may
 * interpret them as local time on some browsers.  This helper ensures
 * consistent UTC interpretation so `toLocaleString()` converts to the
 * user's browser timezone.
 */
export function utcDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  // If the string already has a timezone indicator (Z, +HH:MM, or GMT), use as-is
  if (/[Zz]$/.test(value) || /[+-]\d{2}:\d{2}$/.test(value) || /GMT/i.test(value)) {
    return new Date(value);
  }
  return new Date(value + 'Z');
}

/** Format a backend timestamp for display in the browser's local timezone. */
export function formatTimestamp(value: string | null | undefined): string {
  const d = utcDate(value);
  return d ? d.toLocaleString() : '-';
}

/** Format a backend timestamp as date-only in the browser's local timezone. */
export function formatDate(value: string | null | undefined): string {
  const d = utcDate(value);
  return d ? d.toLocaleDateString() : '-';
}

/** Format a backend timestamp as time-only in the browser's local timezone. */
export function formatTime(value: string | null | undefined): string {
  const d = utcDate(value);
  return d ? d.toLocaleTimeString() : '-';
}
